/**
 * Jarvis tool projections — data nodes that shoot OUT of the core orb (ADR-0025).
 *
 * Iron-Man-style: each agent tool call spawns a glowing edge from the orb centre
 * to a floating node placed around it, the line drawing itself + a flow of dashes
 * running outward, the node materialising at the end. Query totals count up;
 * writes pulse/burst on pending/done/denied. Rendered as an absolute overlay
 * centred on the orb (pointer-events-none).
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Database, Pencil, Plus, Search, Trash2, Check, X } from "lucide-react";

import type { VoicePreview } from "@/lib/portalVoice";

export interface ToolActivity {
  id: string;
  name: string;
  preview?: VoicePreview;
}

const CYAN = "#22d3ee";

// Overlay geometry (box is centred on the orb centre).
const BOX = 480;
const C = BOX / 2; // centre
const R_NODE = 182; // node distance from centre
const R_START = 82; // edge start (just outside the bright core)
// Slots biased to the upper hemisphere + sides, away from the captions below.
const SLOTS = [135, 45, 180, 0, 100, 80];

const KEYFRAMES = `
@keyframes vEdgeDraw{from{stroke-dashoffset:108}to{stroke-dashoffset:0}}
@keyframes vEdgeFlow{to{stroke-dashoffset:-26}}
@keyframes vNodeIn{0%{opacity:0;transform:translate(-50%,-50%) scale(.5)}100%{opacity:1;transform:translate(-50%,-50%) scale(1)}}
@keyframes vSpark{0%{opacity:0;transform:scale(0)}40%{opacity:1}100%{opacity:0;transform:scale(1.8)}}
@keyframes vPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.12)}}
@keyframes vPop{0%{transform:scale(0)}60%{transform:scale(1.3)}100%{transform:scale(1)}}
`;

function useCountUp(target: number, key: string): number {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    if (!Number.isFinite(target) || target <= 0) {
      setN(target > 0 ? target : 0);
      return;
    }
    let raf = 0;
    let start = 0;
    const step = (t: number) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / 700);
      setN(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return n;
}

function opIcon(op: string | undefined, name: string) {
  if (name === "list_tables") return Database;
  switch (op) {
    case "query": return Search;
    case "create": return Plus;
    case "update": return Pencil;
    case "delete": return Trash2;
    default: return Database;
  }
}

function tintOf(p: VoicePreview): string {
  return p.error ? "#f87171" : p.pending ? "#fbbf24" : p.done ? "#34d399" : CYAN;
}

function pos(angleDeg: number, r: number) {
  const a = (angleDeg * Math.PI) / 180;
  return { x: C + r * Math.cos(a), y: C - r * Math.sin(a) };
}

function ToolNode({ item, angle }: { item: ToolActivity; angle: number }) {
  const { t } = useTranslation();
  const p = item.preview ?? {};
  const Icon = opIcon(p.op, item.name);
  const tint = tintOf(p);
  const isQuery = p.op === "query" || item.name === "query_table";
  const count = useCountUp(isQuery && typeof p.total === "number" ? p.total : 0, item.id);
  const node = pos(angle, R_NODE);
  const opLabel = t(`voice.op_${p.op ?? item.name}`, p.op ?? item.name);

  let detail: React.ReactNode = null;
  if (isQuery) {
    detail = (
      <span className="inline-flex items-baseline gap-1">
        <span className="text-lg font-bold tabular-nums" style={{ color: CYAN, textShadow: `0 0 12px ${CYAN}aa` }}>{count}</span>
        <span className="text-[10px] text-cyan-200/70">{t("voice.toolRows", "righe in")} {p.table}</span>
      </span>
    );
  } else if (item.name === "list_tables") {
    detail = <span className="text-[11px] text-cyan-200/70">{t("voice.toolTables", "{{n}} tabelle", { n: p.tables ?? 0 })}</span>;
  } else if (p.error === "denied") {
    detail = <span className="text-[11px] font-medium" style={{ color: tint }}>{t("voice.toolDenied", "permesso negato")}</span>;
  } else if (p.error) {
    detail = <span className="text-[11px] font-medium" style={{ color: tint }}>{t("voice.toolError", "non riuscito")}</span>;
  } else if (p.pending) {
    detail = <span className="text-[11px] font-medium" style={{ color: tint }}>{t("voice.toolPending", "in attesa di conferma")}</span>;
  } else if (p.done) {
    detail = (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: tint }}>
        <Check className="h-3 w-3" style={{ animation: "vPop .4s ease-out" }} />
        {t(`voice.toolDone_${p.op}`, t("voice.toolDoneGeneric", "fatto"))}
      </span>
    );
  }

  return (
    <div
      className="absolute flex max-w-[11rem] items-center gap-2 rounded-xl border px-2.5 py-1.5"
      style={{
        left: node.x,
        top: node.y,
        borderColor: `${tint}66`,
        background: "rgba(7,14,28,0.78)",
        boxShadow: `0 0 22px ${tint}33`,
        backdropFilter: "blur(2px)",
        animation: "vNodeIn .32s cubic-bezier(0.2,0.9,0.2,1) .16s both",
      }}
    >
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
        style={{
          background: `radial-gradient(circle at 32% 30%, ${tint}, ${tint}33)`,
          color: "#06121f",
          animation: p.pending ? "vPulse 1s ease-in-out infinite" : undefined,
        }}
      >
        {p.error ? <X className="h-4 w-4" /> : <Icon className="h-4 w-4" strokeWidth={2.2} />}
      </span>
      <div className="min-w-0 leading-tight">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-cyan-200/70">
          {opLabel}{p.table && !isQuery ? <span className="text-cyan-300/40"> · {p.table}</span> : null}
        </div>
        <div>{detail}</div>
      </div>
    </div>
  );
}

export function VoiceToolStage({ items }: { items: ToolActivity[] }) {
  const shown = items.slice(-SLOTS.length);
  if (!shown.length) return null;
  return (
    <div
      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      style={{ width: BOX, height: BOX }}
    >
      <style>{KEYFRAMES}</style>
      <svg viewBox={`0 0 ${BOX} ${BOX}`} className="absolute inset-0 h-full w-full" style={{ overflow: "visible" }}>
        {shown.map((it, i) => {
          const angle = SLOTS[i];
          const a = pos(angle, R_START);
          const b = pos(angle, R_NODE);
          const tint = tintOf(it.preview ?? {});
          return (
            <g key={it.id}>
              {/* base glow */}
              <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={tint} strokeOpacity={0.18} strokeWidth={4} />
              {/* drawing-in bright core */}
              <line
                x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={tint} strokeWidth={1.6}
                strokeDasharray={108}
                style={{ animation: "vEdgeDraw .35s ease-out both" }}
              />
              {/* outward flowing dashes */}
              <line
                x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={tint} strokeWidth={2.2} strokeLinecap="round"
                strokeDasharray="2 11"
                style={{ animation: "vEdgeFlow 0.9s linear infinite", filter: `drop-shadow(0 0 3px ${tint})` }}
              />
              {/* spark where the edge meets the orb */}
              <circle cx={a.x} cy={a.y} r={3} fill={tint} style={{ animation: "vSpark 1.1s ease-out infinite" }} />
            </g>
          );
        })}
      </svg>
      {shown.map((it, i) => (
        <ToolNode key={it.id} item={it} angle={SLOTS[i]} />
      ))}
    </div>
  );
}
